<template>
  <div id="app" class="h-screen flex flex-col overflow-hidden">
    <!-- Session Warning Component -->
    <SessionWarning />

    <header 
      v-if="showShell"
      class="h-16 flex items-center justify-between gap-2 px-2 sm:px-4 xl:px-6 xl:pl-[164px] bg-[#0d4169] text-white border-b border-white/10 shadow-md shrink-0 relative"
      :class="tabletHeaderSidebarVisible ? 'md:max-xl:ml-[150px]' : 'md:max-xl:ml-0'"
    >
      <div class="flex min-w-0 flex-1 items-center gap-2 sm:gap-3 relative z-10">
        <!-- Hamburger Menu for Mobile -->
        <button 
          class="inline-flex h-9 w-9 shrink-0 xl:hidden p-1.5 hover:bg-white/10 rounded-lg transition-all items-center justify-center"
          @click="toggleSidebar"
          aria-label="Toggle Sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
        <!-- <h1 class="text-sm font-black text-white">Mina Software V. 6.0.1</h1> -->
        <h1 class="min-w-0 whitespace-nowrap text-[clamp(0.68rem,1.6vw,0.875rem)] font-black text-white">
          <span class="md:hidden">Mina Software</span>
          <span class="hidden md:inline">Mina Software V. 6.0.1</span>
          <a
          href="tel:0930481818"
          class="hidden lg:flex items-center justify-center gap-2 text-sm font-bold text-white hover:text-white/90 transition-colors whitespace-nowrap"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="text-white shrink-0"
            aria-hidden="true"
          >
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span>0930481818</span>
        </a>
        </h1>
      </div>

      <!-- Right Side -->
      <div class="flex shrink-0 items-center gap-1 sm:gap-2 xl:gap-4 relative z-10">
        <!-- Branch Switcher (Large Screens Only) -->
        <div v-if="hasAssignedBranches" class="hidden lg:flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full border border-white/20 backdrop-blur-sm hover:bg-white/20 transition-all shadow-md group">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="text-white/70 group-hover:text-white transition-colors"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path><polyline points="9 22 9 12 15 12 15 22"></polyline></svg>
          <select 
            v-model="selectedOrgUnit"
            :disabled="orgUnitSwitching"
            @change="handleBranchChange"
            class="bg-transparent text-white text-[11px] font-black outline-none cursor-pointer hover:text-white transition-colors uppercase tracking-tight min-w-[120px]"
          >
            <option :class="'capitalize'" v-if="hasAssignedCompany" :value="`company:${currentUser.companyId || currentUser.company_id}`" class="text-slate-800 font-semibold bg-white">
               {{ currentUser.company?.name || 'Main Office' }} (Head Office)
            </option>
            <option v-for="branch in assignedBranches" :key="branch.id" :value="`branch:${branch.id}`" class="text-slate-800 font-semibold bg-white">
              {{ branch.name }}
            </option>
          </select>
        </div>

       

        <button
            v-if="dueDaysLeft !== null && dueDaysLeft <= 15"
            type="button"
            class="hidden lg:inline-flex ml-2 px-2 py-0.5 rounded-full text-[11px] font-extrabold border border-white/30 cursor-pointer hover:brightness-110 transition-all"
            :class="dueDaysLeft <= 5 ? 'bg-red-500/20 text-red-100' : 'bg-amber-500/20 text-amber-100'"
            title="Renew subscription"
            @click="openActivationModal"
          >
            {{ dueDaysLeft }} days left
          </button>
          <div
  v-if="currentUser"
  class="flex min-w-0 max-w-[32vw] xl:max-w-none flex-col px-1 sm:px-2 xl:px-4 py-1 rounded-full shadow-sm"
>
  <!-- Company / Branch name -->
  <span class="max-w-full truncate text-[11px] sm:text-sm font-black text-white text-center">
    <u>{{ branchName ? branchName : companyName }}</u> 
  </span>

  <!-- User information -->
  <div class="flex min-w-0 items-center gap-1 sm:gap-2 mt-1">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="shrink-0 text-white/80"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>

    <span class="min-w-0 truncate text-[10px] sm:text-sm">
      {{ currentUser.full_name || currentUser.username }}
    </span>
  </div>
</div>
        
        <template v-if="loginSource !== 'waiter'">
          <!-- Notification Icon -->
          <button
            v-if="canSeeNotifications"
            class="hidden md:inline relative p-2 text-white/70 hover:text-white transition-all group"
            @click="router.push({ name: 'notifications' })"
            title="Notifications"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <!-- Notification Badge -->
            <span 
              v-if="unapprovedCount > 0"
              class="absolute top-1 right-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-[#0d4169] animate-pulse"
            >
              {{ unapprovedCount }}
            </span>
          </button>

          <button
            class="flex shrink-0 items-center gap-1 xl:gap-2 px-1 py-2 text-white/70 hover:text-red-500 transition-all text-xs font-bold"
            @click="handleLogout"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
            Logout
          </button>
        </template>
      </div>
    </header>

    <div v-if="showShell" class="flex-1 overflow-hidden">
      <commonPage>
        <router-view />
      </commonPage>
    </div>
    <div v-else class="h-screen overflow-auto">
      <router-view />
    </div>

    <!-- Activation modal -->
    <div
      v-if="activationModal.open"
      class="fixed inset-0 z-[200] flex items-start justify-center px-4 py-8 overflow-y-auto bg-black/50 backdrop-blur-sm"
      @click.self="closeActivationModal"
    >
      <div
        class="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in fade-in zoom-in duration-300 text-left relative overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-labelledby="activation-modal-title"
      >
        <div class="px-6 py-4 bg-[#0d4169] text-white flex items-center justify-between rounded-t-xl">
          <div class="text-left">
            <h3 id="activation-modal-title" class="text-base font-bold m-0">Renew Subscription</h3>
            <p class="text-xs text-white/80 mt-0.5">Extend by one year plus remaining days</p>
          </div>
          <button
            type="button"
            class="p-1 hover:bg-white/10 rounded-full transition-colors text-white"
            :disabled="activationModal.loading"
            @click="closeActivationModal"
            aria-label="Close activation modal"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div class="p-5 space-y-4">
          <div class="text-xs font-semibold text-slate-500">
            Enter your activation key to extend your subscription by one year plus your remaining {{ dueDaysLeft }} day(s).
          </div>
          <div class="flex flex-col gap-1">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Activation Key</label>
            <input
              v-model.trim="activationModal.activationKey"
              type="text"
              maxlength="9"
              class="px-3 py-2 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm font-mono"
              placeholder="Enter Activation Key"
              :disabled="activationModal.loading"
              @keyup.enter="handleAppActivate"
            />
          </div>
        </div>

        <div class="px-5 py-4 flex items-center justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
            :disabled="activationModal.loading"
            @click="closeActivationModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="px-5 py-2 bg-green-600 text-white text-sm font-bold rounded-md shadow-md hover:bg-green-700 transition-all disabled:opacity-60"
            :disabled="activationModal.loading"
            @click="handleAppActivate"
          >
            {{ activationModal.loading ? 'Activating...' : 'Activate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <Transition name="toast">
      <div v-if="toast.show" class="fixed top-4 right-4 z-[2100] max-w-sm">
        <div
          :class="[
            'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[280px]',
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          ]"
        >
          <span v-if="toast.type === 'success'">✅</span>
          <span v-else>❌</span>
          <span class="font-medium text-sm">{{ toast.message }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import commonPage from './components/commonPage.vue'
import SessionWarning from './components/SessionWarning.vue'
import store from './store'
import { apiFetch, activateSubscriptionInApp, handleForcedLogout, syncServerTime } from './utils/api'
import { parseOrgUnitSelection, switchOrgUnit } from './utils/authOrgUnit'

const route = useRoute()
const router = useRouter()
const unapprovedCount = ref(0)
const loginSource = computed(() => store.getters.loginSource)
const currentUser = computed(() => store.getters.currentUser)
const dueDaysLeft = ref(null)
const activationModal = ref({
  open: false,
  activationKey: '',
  loading: false
})
const companyName = computed(() => currentUser.value?.company?.name || null)
const branchName = computed(() => currentUser.value?.branch?.name || null)
const toast = ref({ show: false, message: '', type: 'success' })
const tabletHeaderSidebarVisible = ref(true)
let toastTimer = null
const assignedBranches = ref([])
const selectedOrgUnit = ref('')
const orgUnitSwitching = ref(false)
let pollInterval = null
let originalDocumentTitle = ''

const hasAssignedBranches = computed(() => {
  const raw = currentUser.value?.branch_ids || currentUser.value?.branchIds || ''
  if (!raw) return false
  return String(raw).split(',').filter(v => v.trim()).length > 0
})

const hasAssignedCompany = computed(() => {
  const companyId = currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null
  if (!companyId) return false
  const raw = currentUser.value?.company_assigned_ids || currentUser.value?.companyAssignedIds || ''
  if (!raw) return false
  const ids = String(raw).split(',').map(v => Number(v.trim())).filter(v => Number.isInteger(v) && v > 0)
  return ids.includes(Number(companyId))
})

const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})

const hasAdminPrivilege = computed(() =>
  userPrivileges.value?.All === true || userPrivileges.value?.all === true
)

const canSeeNotifications = computed(() => {
  if (hasAdminPrivilege.value) return true
  const approvalPrivilege = userPrivileges.value?.APPROVAL || userPrivileges.value?.approval
  return !!approvalPrivilege?.Approve
})



const showShell = computed(() => {
  return route.name !== 'login'
    && route.name !== 'waiter_login'
    && route.name !== 'bill_detail'
    && route.name !== 'barcode_scanner'
    && route.name !== 'qr_scanner'
})

const isAuthorizedForApi = computed(() => {
  return showShell.value && store.getters.isAuthenticated && !!store.state.token
})

function showToast(message, type = 'success') {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { show: true, message, type }
  toastTimer = setTimeout(() => {
    toast.value.show = false
  }, 4000)
}

function openActivationModal() {
  activationModal.value = {
    open: true,
    activationKey: '',
    loading: false
  }
}

function closeActivationModal() {
  if (activationModal.value.loading) return
  activationModal.value.open = false
  activationModal.value.activationKey = ''
}

async function handleAppActivate() {
  const key = String(activationModal.value.activationKey || '').trim()
  if (!key) {
    showToast('Activation key is required.', 'error')
    return
  }

  activationModal.value.loading = true
  try {
    const data = await activateSubscriptionInApp({ activationKey: key })
    activationModal.value.loading = false
    activationModal.value.open = false
    activationModal.value.activationKey = ''
    showToast(data.message || 'Activation successful. Your subscription has been renewed.')
    await fetchDueDaysLeft()
  } catch (err) {
    showToast(err.message || 'Activation failed', 'error')
  } finally {
    activationModal.value.loading = false
  }
}

async function fetchDueDaysLeft() {
  if (!isAuthorizedForApi.value) {
    dueDaysLeft.value = null
    return
  }
  try {
    const u = store.getters.currentUser
    if (!u) {
      dueDaysLeft.value = null
      return
    }

    const res = await apiFetch('/users/due-days-left')
    const contentType = res.headers.get('content-type') || ''
    const data = contentType.includes('application/json')
      ? await res.json()
      : {}

    if (!res.ok) {
      if (res.status === 403 && (data?.code === 'SUBSCRIPTION_EXPIRED' || data?.forceLogout)) {
        dueDaysLeft.value = null
        await handleForcedLogout(
          'subscription',
          data?.message ||
            'Your subscription period has ended. Please contact your administrator to renew.'
        )
        return
      }
      dueDaysLeft.value = null
      return
    }

    const days = data?.dueDaysLeft
    if (days === null || days === undefined) {
      dueDaysLeft.value = null
      return
    }

    const n = Number(days)
    if (!Number.isFinite(n)) {
      dueDaysLeft.value = null
      return
    }

    if (n < 0) {
      dueDaysLeft.value = null
      await handleForcedLogout(
        'subscription',
        'Your subscription period has ended. Please contact your administrator to renew.'
      )
      return
    }

    dueDaysLeft.value = n
  } catch (err) {
    if (String(err?.message || '').includes('subscription') || String(err?.message || '').includes('Subscription')) {
      return
    }
    console.error('Error fetching due days left in App.vue:', err)
    dueDaysLeft.value = null
  }
}

async function fetchUnapprovedCount() {
  if (!isAuthorizedForApi.value) {
    unapprovedCount.value = 0
    return
  }
  if (!canSeeNotifications.value) {
    unapprovedCount.value = 0
    return
  }
  try {
    const companyId = store.getters.currentUser?.companyId || store.getters.currentUser?.company_id || null
    const branchId = store.getters.currentUser?.branchId || store.getters.currentUser?.branch_id || null
    if (!companyId && !branchId) return

    const query = branchId ? `branchId=${branchId}` : `companyId=${companyId}`

    const [srRes, retRes, prRes] = await Promise.all([
      apiFetch('/store-request/pending'),
      apiFetch(`/return?${query}&isApproved=0`),
      apiFetch(`/purchase-requisition?${query}&isApproved=0`)
    ])

    let totalCount = 0

    const safeJson = async (res) => {
      if (!res?.ok) return null
      try {
        return await res.json()
      } catch {
        return null
      }
    }

    if (srRes.ok) {
      const data = await safeJson(srRes)
      const list = data?.data || []
      const depts = new Set()
      list.forEach(item => { if (item.department) depts.add(item.department) })
      totalCount += depts.size
    }

    if (retRes.ok) {
      const list = await safeJson(retRes)
      const depts = new Set()
      ;(Array.isArray(list) ? list : []).forEach(item => { if (item.department?.name) depts.add(item.department.name) })
      totalCount += depts.size
    }

    if (prRes.ok) {
      const list = await safeJson(prRes)
      const depts = new Set()
      ;(Array.isArray(list) ? list : []).forEach(item => { if (item.department?.name) depts.add(item.department.name) })
      totalCount += depts.size
    }

    unapprovedCount.value = totalCount
  } catch (err) {
    const msg = String(err?.message || '')
    if (msg !== 'Not authenticated' && msg !== 'Session expired' && !msg.includes('Session expired')) {
      console.error('Error fetching unapproved count in App.vue:', err)
    }
  }
}

async function fetchAssignedBranches() {
  if (!isAuthorizedForApi.value) return
  const user = currentUser.value
  if (!user) return
  
  const raw = user.branch_ids || user.branchIds || ''
  if (!raw) return
  
  const ids = String(raw).split(',').map(v => Number(v.trim())).filter(id => id > 0)
  
  if (ids.length > 0) {
    const results = await Promise.all(
      ids.map(async (id) => {
        try {
          const res = await apiFetch(`/branches/${id}`)
          if (!res.ok) return null
          const data = await res.json()
          return data?.id ? data : null
        } catch {
          return null
        }
      })
    )
    assignedBranches.value = results.filter(Boolean)
    
    // Set initial selection
    const currentBranchId = user.branchId || user.branch_id
    if (currentBranchId) {
      selectedOrgUnit.value = `branch:${currentBranchId}`
    } else if (hasAssignedCompany.value) {
       selectedOrgUnit.value = `company:${user.companyId || user.company_id}`
    }
  }
}

async function handleBranchChange() {
  const parsed = parseOrgUnitSelection(selectedOrgUnit.value)
  if (!parsed || orgUnitSwitching.value) return

  orgUnitSwitching.value = true
  try {
    const data = await switchOrgUnit({
      orgType: parsed.type,
      orgId: parsed.selectedId,
    })

    store.commit('setAuth', {
      token: data.token,
      user: data.user,
      source: store.state.loginSource
    })

    if (data.dueDaysLeft !== null && data.dueDaysLeft !== undefined) {
      const n = Number(data.dueDaysLeft)
      dueDaysLeft.value = Number.isFinite(n) ? n : null
    }

    window.location.reload()
  } catch (err) {
    console.error('Error switching organization unit:', err)
    const currentBranchId = currentUser.value?.branchId || currentUser.value?.branch_id
    if (currentBranchId) {
      selectedOrgUnit.value = `branch:${currentBranchId}`
    } else if (hasAssignedCompany.value) {
      selectedOrgUnit.value = `company:${currentUser.value?.companyId || currentUser.value?.company_id}`
    }
    showToast(err.message || 'Failed to switch organization unit', 'error')
  } finally {
    orgUnitSwitching.value = false
  }
}

function handleLogout() {
  const source = store.getters.loginSource
  store.dispatch('logout')
  if (source === 'waiter') {
    router.push({ name: 'waiter_login' })
  } else {
    router.push({ name: 'login' })
  }
}

function toggleSidebar() {
  window.dispatchEvent(new CustomEvent('toggle-sidebar'))
}

function handleTabletSidebarVisibility(event) {
  tabletHeaderSidebarVisible.value = event.detail !== false
}

function handleBeforePrint() {
  originalDocumentTitle = document.title
  document.title = ''
}

function handleAfterPrint() {
  if (originalDocumentTitle) {
    document.title = originalDocumentTitle
  }
}

function startBackgroundPolling() {
  if (pollInterval || !isAuthorizedForApi.value) return
  pollInterval = setInterval(() => {
    if (!isAuthorizedForApi.value) return
    fetchUnapprovedCount()
    fetchDueDaysLeft()
  }, 30000)
}

function stopBackgroundPolling() {
  if (pollInterval) {
    clearInterval(pollInterval)
    pollInterval = null
  }
}

function refreshAuthorizedAppData() {
  if (!isAuthorizedForApi.value) {
    unapprovedCount.value = 0
    dueDaysLeft.value = null
    stopBackgroundPolling()
    return
  }
  fetchUnapprovedCount()
  fetchDueDaysLeft()
  fetchAssignedBranches()
  startBackgroundPolling()
}

watch(
  () => [isAuthorizedForApi.value, route.name],
  () => {
    refreshAuthorizedAppData()
  }
)

onMounted(() => {
  syncServerTime({ force: true }).catch(() => {})
  refreshAuthorizedAppData()
  window.addEventListener('tablet-sidebar-visibility', handleTabletSidebarVisibility)

  // Add activity listeners to update session on user interaction


  // Hide app title in browser print header/footer across all pages.
  window.addEventListener('beforeprint', handleBeforePrint)
  window.addEventListener('afterprint', handleAfterPrint)
})

onBeforeUnmount(() => {
  stopBackgroundPolling()
  if (toastTimer) clearTimeout(toastTimer)
  window.removeEventListener('tablet-sidebar-visibility', handleTabletSidebarVisibility)

  // Remove activity listeners


  window.removeEventListener('beforeprint', handleBeforePrint)
  window.removeEventListener('afterprint', handleAfterPrint)
})

watch(
  () => store.getters.currentUser,
  () => {
    fetchDueDaysLeft()
    fetchAssignedBranches()
  }
)
</script>

<style>
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #1f2933;
}

.app-header {
  color: #f9fafb;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.4);
}

.app-navbar {
  max-width: 960px;
  margin: 0 auto;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.app-title {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.app-main {
  padding: 24px 12px;
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

</style>


<!-- 
unit price in item register
choose quant from the same item but diff price in siv when approved exist
variant must number 1,2...
item code must unit of measure in grv

 -->